import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import {
  LEDGER_PAGE_SIZE,
  useCreditLedger,
  useCreditsBalance,
  creditsToUsd,
  formatCredits,
  type LedgerRange,
} from '../hooks/useCredits';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import EmailVerifyPromoCard from '../components/EmailVerifyPromoCard';

const REASON_COLOR: Record<string, 'default' | 'success' | 'error' | 'info' | 'warning'> = {
  usage: 'default',
  refund: 'info',
  admin_grant: 'success',
  purchase: 'success',
  adjustment: 'warning',
};

type RangePreset = 'all' | '7d' | '30d' | '90d' | 'custom';
const PRESET_DAYS: Record<'7d' | '30d' | '90d', number> = { '7d': 7, '30d': 30, '90d': 90 };
const PAGE_SIZES = [25, 50, 100];

// `YYYY-MM-DD` from a date input → the instant of that local midnight.
function localDayStart(day: string, addDays = 0): string | undefined {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d + addDays).toISOString();
}

// Custom range is inclusive of both picked days, so `to` is the next midnight.
function customRange(fromDay: string, toDay: string): LedgerRange {
  return { from: localDayStart(fromDay), to: localDayStart(toDay, 1) };
}

export default function Credits() {
  const t = useTranslation();
  const { credits } = useCreditsBalance({ pollMs: 30_000 });
  const [preset, setPreset] = useState<RangePreset>('all');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  // Held in state (not derived per render) so a rolling preset keeps a stable
  // window instead of refetching every render.
  const [range, setRange] = useState<LedgerRange>({});
  const [pageSize, setPageSize] = useState(LEDGER_PAGE_SIZE);
  const { items, total, page, setPage, loading, error } = useCreditLedger({ pollMs: 15_000, pageSize, range });
  const filtered = range.from != null || range.to != null;

  const choosePreset = (next: RangePreset | null) => {
    if (!next) return;
    setPreset(next);
    if (next === 'all') setRange({});
    else if (next === 'custom') setRange(customRange(fromDay, toDay));
    else setRange({ from: new Date(Date.now() - PRESET_DAYS[next] * 86_400_000).toISOString() });
  };
  const changeDay = (which: 'from' | 'to', day: string) => {
    const nextFrom = which === 'from' ? day : fromDay;
    const nextTo = which === 'to' ? day : toDay;
    setFromDay(nextFrom);
    setToDay(nextTo);
    setRange(customRange(nextFrom, nextTo));
  };

  return (
    <Box>
      <PageHeader title={t.credits.title} description={t.credits.description} />
      <EmailVerifyPromoCard />
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 3, mb: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>
            {t.credits.balance}
          </Typography>
          <Typography sx={{ fontSize: 36, fontWeight: 700, mt: 0.5 }}>
            {credits == null ? '—' : formatCredits(credits)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {credits == null
              ? ' '
              : t.credits.usdEquivalent.replace('{value}', creditsToUsd(credits))}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            component={RouterLink}
            to="/payment"
            fullWidth
            sx={{ mt: 2 }}
          >
            {t.credits.getMore}
          </Button>
        </Paper>
      </Box>

      <Section title={t.credits.historyTitle}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, v: RangePreset | null) => choosePreset(v)}
            aria-label={t.credits.filter.label}
          >
            <ToggleButton value="all">{t.credits.filter.all}</ToggleButton>
            <ToggleButton value="7d">{t.credits.filter.last7}</ToggleButton>
            <ToggleButton value="30d">{t.credits.filter.last30}</ToggleButton>
            <ToggleButton value="90d">{t.credits.filter.last90}</ToggleButton>
            <ToggleButton value="custom">{t.credits.filter.custom}</ToggleButton>
          </ToggleButtonGroup>
          {preset === 'custom' && (
            <Stack direction="row" spacing={1}>
              <TextField
                type="date"
                size="small"
                label={t.credits.filter.from}
                value={fromDay}
                onChange={(e) => changeDay('from', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDay || undefined }}
              />
              <TextField
                type="date"
                size="small"
                label={t.credits.filter.to}
                value={toDay}
                onChange={(e) => changeDay('to', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDay || undefined }}
              />
            </Stack>
          )}
        </Stack>
        <Paper sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 3 }}>{t.credits.table.when}</TableCell>
                <TableCell>{t.credits.table.reason}</TableCell>
                <TableCell>{t.credits.table.endpoint}</TableCell>
                <TableCell align="right">{t.credits.table.delta}</TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>{t.credits.table.balance}</TableCell>
                <TableCell>{t.credits.table.note}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {filtered ? t.credits.historyEmptyFiltered : t.credits.historyEmpty}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>
                      {new Date(it.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.credits.reasonLabels[it.reason] ?? it.reason}
                        color={REASON_COLOR[it.reason] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: 'text.secondary' }}>
                      {it.endpoint ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: it.delta < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                      {it.delta > 0 ? '+' : ''}{formatCredits(it.delta)}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 3, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCredits(it.balance_after)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                      <Stack spacing={0.25}>
                        {it.note && <Box>{it.note}</Box>}
                        {it.activity_log_id && <Box sx={{ fontSize: 11 }}>activity #{it.activity_log_id}</Box>}
                        {it.payment_id && <Box sx={{ fontSize: 11 }}>payment #{it.payment_id}</Box>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
              labelRowsPerPage={t.credits.pagination.rowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                t.credits.pagination.displayed
                  .replace('{from}', String(from))
                  .replace('{to}', String(to))
                  .replace('{count}', String(count))
              }
            />
          )}
        </Paper>
      </Section>
    </Box>
  );
}
